import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { RequestServiceResponse } from '@tcc-online/request-service';
import { AppState } from '../../reducers/root.reducer';
import IBaseState from '../../types/IBaseState';
import UploadState from '../../types/UploadState';
import { createNotifiedThunk } from '../../reducers/createAppAsyncThunk';
import AdminTeamService from '../services/AdminTeamService';
import AdminTeam from '../types/team/AdminTeam';
import AdminTeamParticipant from '../types/team/AdminTeamParticipant';
import IPaginationState from '../../types/state/IPaginationState';
import IPagination from '../../types/IPagination';
import { Report, ReportResult } from '@tcc-online/output-report';

const NAME = 'AdminTeam';

const getTeams = createNotifiedThunk(
    `${NAME}/getTeams`,
    AdminTeamService.getTeams,
    {
        error: 'fe.admin.error.load-teams',
    },
);
const getTeam = createNotifiedThunk(
    `${NAME}/getTeam`,
    AdminTeamService.getTeam,
    {
        error: 'fe.admin.error.load-team',
    },
);
const createTeam = createNotifiedThunk(
    `${NAME}/createTeam`,
    AdminTeamService.createTeam,
    { error: 'fe.admin.error.create-team' },
);
const updateTeam = createNotifiedThunk(
    `${NAME}/updateTeam`,
    AdminTeamService.updateTeam,
    { error: 'fe.admin.error.update-team' },
);
const deleteTeam = createNotifiedThunk(
    `${NAME}/deleteTeam`,
    AdminTeamService.deleteTeam,
    { error: 'fe.admin.error.delete-team' },
);
const copyTeamToProject = createNotifiedThunk(
    `${NAME}/copyTeamToProject`,
    AdminTeamService.copyTeamToProject,
    { error: 'fe.admin.error.copy-team-to-project' },
);

const getProjectTeams = createNotifiedThunk(
    `${NAME}/getProjectTeams`,
    AdminTeamService.getProjectTeams,
    { error: 'fe.admin.error.load-project-teams' },
);
const getProjectTeam = createNotifiedThunk(
    `${NAME}/getProjectTeam`,
    AdminTeamService.getProjectTeam,
    { error: 'fe.admin.error.load-project-team' },
);
const createProjectTeam = createNotifiedThunk(
    `${NAME}/createProjectTeam`,
    AdminTeamService.createProjectTeam,
    { error: 'fe.admin.error.create-project-team' },
);
const updateProjectTeam = createNotifiedThunk(
    `${NAME}/updateProjectTeam`,
    AdminTeamService.updateProjectTeam,
    { error: 'fe.admin.error.update-project-team' },
);
const deleteProjectTeam = createNotifiedThunk(
    `${NAME}/deleteProjectTeam`,
    AdminTeamService.deleteProjectTeam,
    { error: 'fe.admin.error.delete-project-team' },
);
const copyProjectTeamToCompany = createNotifiedThunk(
    `${NAME}/copyProjectTeamToCompany`,
    AdminTeamService.copyProjectTeamToCompany,
    { error: 'fe.admin.error.copy-team-to-company' },
);
const addTeamParticipant = createNotifiedThunk(
    `${NAME}/addTeamParticipant`,
    AdminTeamService.addTeamParticipant,
    { error: 'fe.admin.error.add-participant-to-team' },
);
const addProjectTeamParticipant = createNotifiedThunk(
    `${NAME}/addProjectTeamParticipant`,
    AdminTeamService.addProjectTeamParticipant,
    { error: 'fe.admin.error.add-participant-to-project-team' },
);
const getTeamParticipants = createNotifiedThunk(
    `${NAME}/getTeamParticipants`,
    AdminTeamService.getTeamParticipants,
    { error: 'fe.admin.error.load-team-participants' },
);
const getTeamParticipant = createNotifiedThunk(
    `${NAME}/getTeamParticipant`,
    AdminTeamService.getTeamParticipant,
    { error: 'fe.admin.error.load-team-participant' },
);
const updateTeamParticipant = createNotifiedThunk(
    `${NAME}/updateTeamParticipant`,
    AdminTeamService.updateTeamParticipant,
    { error: 'fe.admin.error.update-team-participant' },
);
const deleteTeamParticipant = createNotifiedThunk(
    `${NAME}/deleteTeamParticipant`,
    AdminTeamService.deleteTeamParticipant,
    { error: 'fe.admin.error.remove-participant-from-team' },
);
const getProjectTeamParticipants = createNotifiedThunk(
    `${NAME}/getProjectTeamParticipants`,
    AdminTeamService.getProjectTeamParticipants,
    { error: 'fe.admin.error.load-project-team-participants' },
);
const getProjectTeamParticipant = createNotifiedThunk(
    `${NAME}/getProjectTeamParticipant`,
    AdminTeamService.getProjectTeamParticipant,
    { error: 'fe.admin.error.load-project-team-participant' },
);
const updateProjectTeamParticipant = createNotifiedThunk(
    `${NAME}/updateProjectTeamParticipant`,
    AdminTeamService.updateProjectTeamParticipant,
    { error: 'fe.admin.error.update-project-team-participant' },
);
const deleteProjectTeamParticipant = createNotifiedThunk(
    `${NAME}/deleteProjectTeamParticipant`,
    AdminTeamService.deleteProjectTeamParticipant,
    { error: 'fe.admin.error.remove-participant-from-project-team' },
);

const getTeamReports = createNotifiedThunk(
    `${NAME}/getTeamReports`,
    AdminTeamService.getTeamReports,
    { error: 'fe.admin.error.load-team-reports' },
);
const getTeamReportsByType = createNotifiedThunk(
    `${NAME}/getTeamReportsByType`,
    AdminTeamService.getTeamReportsByType,
    { error: 'fe.admin.error.load-team-reports' },
);
const getTeamReport = createNotifiedThunk(
    `${NAME}/getTeamReport`,
    AdminTeamService.getTeamReport,
    { error: 'fe.admin.error.load-team-report' },
);
const getTeamReportResult = createNotifiedThunk(
    `${NAME}/getTeamReportResult`,
    AdminTeamService.getTeamReportResult,
    { error: 'fe.admin.error.load-team-report-result' },
);
const getProjectTeamReports = createNotifiedThunk(
    `${NAME}/getProjectTeamReports`,
    AdminTeamService.getProjectTeamReports,
    { error: 'fe.admin.error.load-project-team-reports' },
);
const getProjectTeamReport = createNotifiedThunk(
    `${NAME}/getProjectTeamReport`,
    AdminTeamService.getProjectTeamReport,
    { error: 'fe.admin.error.load-project-team-report' },
);
const getProjectTeamReportResult = createNotifiedThunk(
    `${NAME}/getProjectTeamReportResult`,
    AdminTeamService.getProjectTeamReportResult,
    { error: 'fe.admin.error.load-project-team-report-result' },
);

export interface AdminTeamState {
    list: IPaginationState<AdminTeam[]> & UploadState<AdminTeam[]>;
    detail: IBaseState<AdminTeam | null>;
    participantDetail: IBaseState<AdminTeamParticipant | null>;
    reports: IBaseState<Report[]>;
    reportsByType: Record<number, IBaseState<Report[]>>;
    reportDetail: IBaseState<Report | null>;
    reportResult: IBaseState<ReportResult | null>;
}

const initialState: AdminTeamState = {
    list: {
        data: [],
        loading: false,
        pagination: { page: 1 },
        uploading: false,
    },
    detail: {
        data: null,
        loading: false,
    },
    participantDetail: {
        data: null,
        loading: false,
    },
    reports: {
        data: [],
        loading: false,
    },
    reportsByType: {},
    reportDetail: {
        data: null,
        loading: false,
    },
    reportResult: {
        data: null,
        loading: false,
    },
};

const slice = createSlice({
    name: NAME,
    initialState,
    reducers: {},
    extraReducers: (builder): void => {
        builder.addCase(getTeams.pending, (state: AdminTeamState): void => {
            state.list.data = [];
        });
        builder.addCase(
            getTeams.fulfilled,
            (
                state: AdminTeamState,
                action: PayloadAction<
                    RequestServiceResponse<AdminTeam[]> & {
                        pagination?: IPagination;
                    }
                >,
            ): void => {
                state.list.data = action.payload.data;
                state.list.pagination = action.payload.pagination ?? {
                    page: 1,
                };
            },
        );

        builder.addCase(
            getProjectTeams.pending,
            (state: AdminTeamState): void => {
                state.list.data = [];
            },
        );
        builder.addCase(
            getProjectTeams.fulfilled,
            (
                state: AdminTeamState,
                action: PayloadAction<
                    RequestServiceResponse<AdminTeam[]> & {
                        pagination?: IPagination;
                    }
                >,
            ): void => {
                state.list.data = action.payload.data;
                state.list.pagination = action.payload.pagination ?? {
                    page: 1,
                };
            },
        );

        builder.addCase(getTeam.pending, (state: AdminTeamState): void => {
            state.detail.data = null;
        });
        builder.addCase(
            getTeam.fulfilled,
            (
                state: AdminTeamState,
                action: PayloadAction<RequestServiceResponse<AdminTeam>>,
            ): void => {
                state.detail.data = action.payload.data;
            },
        );

        builder.addCase(
            getProjectTeam.pending,
            (state: AdminTeamState): void => {
                state.detail.data = null;
            },
        );
        builder.addCase(
            getProjectTeam.fulfilled,
            (
                state: AdminTeamState,
                action: PayloadAction<RequestServiceResponse<AdminTeam>>,
            ): void => {
                state.detail.data = action.payload.data;
            },
        );

        builder.addCase(
            updateTeam.fulfilled,
            (
                state: AdminTeamState,
                action: PayloadAction<RequestServiceResponse<AdminTeam>>,
            ): void => {
                const updated = action.payload.data;
                const index = state.list.data.findIndex(
                    (team: AdminTeam) => team.id === updated.id,
                );
                if (index >= 0) {
                    state.list.data[index] = updated;
                }
            },
        );

        builder.addCase(
            updateProjectTeam.fulfilled,
            (
                state: AdminTeamState,
                action: PayloadAction<RequestServiceResponse<AdminTeam>>,
            ): void => {
                const updated = action.payload.data;
                const index = state.list.data.findIndex(
                    (team: AdminTeam) => team.id === updated.id,
                );
                if (index >= 0) {
                    state.list.data[index] = updated;
                }
            },
        );

        builder.addCase(
            addTeamParticipant.fulfilled,
            (
                state: AdminTeamState,
                action: PayloadAction<
                    RequestServiceResponse<AdminTeamParticipant>
                >,
            ): void => {
                state.detail.data?.participants.push(action.payload.data);
            },
        );

        builder.addCase(
            addProjectTeamParticipant.fulfilled,
            (
                state: AdminTeamState,
                action: PayloadAction<
                    RequestServiceResponse<AdminTeamParticipant>
                >,
            ): void => {
                state.detail.data?.participants.push(action.payload.data);
            },
        );

        builder.addCase(
            getTeamParticipants.fulfilled,
            (
                state: AdminTeamState,
                action: PayloadAction<
                    RequestServiceResponse<AdminTeamParticipant[]>
                >,
            ): void => {
                if (state.detail.data) {
                    state.detail.data.participants = action.payload.data;
                }
            },
        );

        builder.addCase(
            getProjectTeamParticipants.fulfilled,
            (
                state: AdminTeamState,
                action: PayloadAction<
                    RequestServiceResponse<AdminTeamParticipant[]>
                >,
            ): void => {
                if (state.detail.data) {
                    state.detail.data.participants = action.payload.data;
                }
            },
        );

        builder.addCase(
            getTeamParticipant.pending,
            (state: AdminTeamState): void => {
                state.participantDetail.data = null;
            },
        );
        builder.addCase(
            getTeamParticipant.fulfilled,
            (
                state: AdminTeamState,
                action: PayloadAction<
                    RequestServiceResponse<AdminTeamParticipant>
                >,
            ): void => {
                state.participantDetail.data = action.payload.data;
            },
        );

        builder.addCase(
            getProjectTeamParticipant.pending,
            (state: AdminTeamState): void => {
                state.participantDetail.data = null;
            },
        );
        builder.addCase(
            getProjectTeamParticipant.fulfilled,
            (
                state: AdminTeamState,
                action: PayloadAction<
                    RequestServiceResponse<AdminTeamParticipant>
                >,
            ): void => {
                state.participantDetail.data = action.payload.data;
            },
        );

        builder.addCase(
            updateTeamParticipant.fulfilled,
            (
                state: AdminTeamState,
                action: PayloadAction<
                    RequestServiceResponse<AdminTeamParticipant>
                >,
            ): void => {
                const updated = action.payload.data;
                const index =
                    state.detail.data?.participants.findIndex(
                        (participant: AdminTeamParticipant) =>
                            participant.user.id === updated.user.id,
                    ) ?? -1;
                if (index >= 0) {
                    state.detail.data!.participants[index] = updated;
                }
                if (state.participantDetail.data?.user.id === updated.user.id) {
                    state.participantDetail.data = updated;
                }
            },
        );

        builder.addCase(
            updateProjectTeamParticipant.fulfilled,
            (
                state: AdminTeamState,
                action: PayloadAction<
                    RequestServiceResponse<AdminTeamParticipant>
                >,
            ): void => {
                const updated = action.payload.data;
                const index =
                    state.detail.data?.participants.findIndex(
                        (participant: AdminTeamParticipant) =>
                            participant.user.id === updated.user.id,
                    ) ?? -1;
                if (index >= 0) {
                    state.detail.data!.participants[index] = updated;
                }
                if (state.participantDetail.data?.user.id === updated.user.id) {
                    state.participantDetail.data = updated;
                }
            },
        );

        builder.addCase(
            deleteTeamParticipant.fulfilled,
            (state: AdminTeamState, action): void => {
                const { participantId } = action.meta.arg;
                if (state.detail.data) {
                    const index = state.detail.data.participants.findIndex(
                        (participant: AdminTeamParticipant) =>
                            participant.user.id === participantId,
                    );

                    if (index >= 0) {
                        state.detail.data.participants.splice(index, 1);
                    }
                }
            },
        );

        builder.addCase(
            deleteProjectTeamParticipant.fulfilled,
            (state: AdminTeamState, action): void => {
                const { participantId } = action.meta.arg;
                if (state.detail.data) {
                    const index = state.detail.data.participants.findIndex(
                        (participant: AdminTeamParticipant) =>
                            participant.user.id === participantId,
                    );
                    if (index >= 0) {
                        state.detail.data.participants.splice(index, 1);
                    }
                }
            },
        );

        builder.addCase(
            createTeam.fulfilled,
            (
                state: AdminTeamState,
                action: PayloadAction<RequestServiceResponse<AdminTeam>>,
            ): void => {
                state.list.data.push(action.payload.data);
            },
        );

        builder.addCase(
            createProjectTeam.fulfilled,
            (
                state: AdminTeamState,
                action: PayloadAction<RequestServiceResponse<AdminTeam>>,
            ): void => {
                state.list.data.push(action.payload.data);
            },
        );

        builder.addCase(
            deleteTeam.fulfilled,
            (state: AdminTeamState, action): void => {
                const index = state.list.data.findIndex(
                    (team: AdminTeam) => team.id === action.meta.arg,
                );
                if (index >= 0) {
                    state.list.data.splice(index, 1);
                }
            },
        );

        builder.addCase(
            deleteProjectTeam.fulfilled,
            (state: AdminTeamState, action): void => {
                const index = state.list.data.findIndex(
                    (team: AdminTeam) => team.id === action.meta.arg.teamId,
                );
                if (index >= 0) {
                    state.list.data.splice(index, 1);
                }
            },
        );

        builder.addCase(
            getTeamReports.pending,
            (state: AdminTeamState): void => {
                state.reports.data = [];
            },
        );
        builder.addCase(
            getTeamReports.fulfilled,
            (
                state: AdminTeamState,
                action: PayloadAction<RequestServiceResponse<Report[]>>,
            ): void => {
                state.reports.data = action.payload.data;
            },
        );

        builder.addCase(
            getProjectTeamReports.pending,
            (state: AdminTeamState): void => {
                state.reports.data = [];
            },
        );
        builder.addCase(
            getProjectTeamReports.fulfilled,
            (
                state: AdminTeamState,
                action: PayloadAction<RequestServiceResponse<Report[]>>,
            ): void => {
                state.reports.data = action.payload.data;
            },
        );

        builder.addCase(
            getTeamReportsByType.pending,
            (state: AdminTeamState, action): void => {
                const { reportType } = action.meta.arg;
                const existing = state.reportsByType[reportType];
                state.reportsByType[reportType] = {
                    data: existing?.data ?? [],
                    loading: true,
                };
            },
        );
        builder.addCase(
            getTeamReportsByType.fulfilled,
            (state: AdminTeamState, action): void => {
                const { reportType } = action.meta.arg;
                state.reportsByType[reportType] = {
                    data: action.payload.data,
                    loading: false,
                };
            },
        );
        builder.addCase(
            getTeamReportsByType.rejected,
            (state: AdminTeamState, action): void => {
                const { reportType } = action.meta.arg;
                const existing = state.reportsByType[reportType];
                state.reportsByType[reportType] = {
                    data: existing?.data ?? [],
                    loading: false,
                };
            },
        );

        builder.addCase(
            getTeamReport.pending,
            (state: AdminTeamState): void => {
                state.reportDetail.data = null;
            },
        );
        builder.addCase(
            getTeamReport.fulfilled,
            (
                state: AdminTeamState,
                action: PayloadAction<RequestServiceResponse<Report>>,
            ): void => {
                state.reportDetail.data = action.payload.data;
            },
        );

        builder.addCase(
            getProjectTeamReport.pending,
            (state: AdminTeamState): void => {
                state.reportDetail.data = null;
            },
        );
        builder.addCase(
            getProjectTeamReport.fulfilled,
            (
                state: AdminTeamState,
                action: PayloadAction<RequestServiceResponse<Report>>,
            ): void => {
                state.reportDetail.data = action.payload.data;
            },
        );

        builder.addCase(
            getTeamReportResult.pending,
            (state: AdminTeamState): void => {
                state.reportResult.data = null;
            },
        );
        builder.addCase(
            getTeamReportResult.fulfilled,
            (
                state: AdminTeamState,
                action: PayloadAction<RequestServiceResponse<ReportResult>>,
            ): void => {
                state.reportResult.data = action.payload.data;
            },
        );

        builder.addCase(
            getProjectTeamReportResult.pending,
            (state: AdminTeamState): void => {
                state.reportResult.data = null;
            },
        );
        builder.addCase(
            getProjectTeamReportResult.fulfilled,
            (
                state: AdminTeamState,
                action: PayloadAction<RequestServiceResponse<ReportResult>>,
            ): void => {
                state.reportResult.data = action.payload.data;
            },
        );

        builder.addMatcher(
            isAnyOf(getTeams.pending, getProjectTeams.pending),
            (state: AdminTeamState): void => {
                state.list.loading = true;
            },
        );

        builder.addMatcher(
            isAnyOf(
                getTeams.fulfilled,
                getTeams.rejected,
                getProjectTeams.fulfilled,
                getProjectTeams.rejected,
            ),
            (state: AdminTeamState): void => {
                state.list.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(
                getTeam.pending,
                getProjectTeam.pending,
                getTeamParticipants.pending,
                getProjectTeamParticipants.pending,
            ),
            (state: AdminTeamState): void => {
                state.detail.loading = true;
            },
        );

        builder.addMatcher(
            isAnyOf(
                getTeam.fulfilled,
                getTeam.rejected,
                getProjectTeam.fulfilled,
                getProjectTeam.rejected,
                getTeamParticipants.fulfilled,
                getTeamParticipants.rejected,
                getProjectTeamParticipants.fulfilled,
                getProjectTeamParticipants.rejected,
            ),
            (state: AdminTeamState): void => {
                state.detail.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(
                getTeamParticipant.pending,
                getProjectTeamParticipant.pending,
            ),
            (state: AdminTeamState): void => {
                state.participantDetail.loading = true;
            },
        );

        builder.addMatcher(
            isAnyOf(
                getTeamParticipant.fulfilled,
                getTeamParticipant.rejected,
                getProjectTeamParticipant.fulfilled,
                getProjectTeamParticipant.rejected,
            ),
            (state: AdminTeamState): void => {
                state.participantDetail.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(getTeamReports.pending, getProjectTeamReports.pending),
            (state: AdminTeamState): void => {
                state.reports.loading = true;
            },
        );

        builder.addMatcher(
            isAnyOf(
                getTeamReports.fulfilled,
                getTeamReports.rejected,
                getProjectTeamReports.fulfilled,
                getProjectTeamReports.rejected,
            ),
            (state: AdminTeamState): void => {
                state.reports.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(getTeamReport.pending, getProjectTeamReport.pending),
            (state: AdminTeamState): void => {
                state.reportDetail.loading = true;
            },
        );

        builder.addMatcher(
            isAnyOf(
                getTeamReport.fulfilled,
                getTeamReport.rejected,
                getProjectTeamReport.fulfilled,
                getProjectTeamReport.rejected,
            ),
            (state: AdminTeamState): void => {
                state.reportDetail.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(
                getTeamReportResult.pending,
                getProjectTeamReportResult.pending,
            ),
            (state: AdminTeamState): void => {
                state.reportResult.loading = true;
            },
        );

        builder.addMatcher(
            isAnyOf(
                getTeamReportResult.fulfilled,
                getTeamReportResult.rejected,
                getProjectTeamReportResult.fulfilled,
                getProjectTeamReportResult.rejected,
            ),
            (state: AdminTeamState): void => {
                state.reportResult.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(
                createTeam.pending,
                updateTeam.pending,
                deleteTeam.pending,
                copyTeamToProject.pending,
                addTeamParticipant.pending,
                updateTeamParticipant.pending,
                deleteTeamParticipant.pending,
                createProjectTeam.pending,
                updateProjectTeam.pending,
                deleteProjectTeam.pending,
                copyProjectTeamToCompany.pending,
                addProjectTeamParticipant.pending,
                updateProjectTeamParticipant.pending,
                deleteProjectTeamParticipant.pending,
            ),
            (state: AdminTeamState): void => {
                state.list.uploading = true;
            },
        );

        builder.addMatcher(
            isAnyOf(
                createTeam.fulfilled,
                createTeam.rejected,
                updateTeam.fulfilled,
                updateTeam.rejected,
                deleteTeam.fulfilled,
                deleteTeam.rejected,
                copyTeamToProject.fulfilled,
                copyTeamToProject.rejected,
                addTeamParticipant.fulfilled,
                addTeamParticipant.rejected,
                updateTeamParticipant.fulfilled,
                updateTeamParticipant.rejected,
                deleteTeamParticipant.fulfilled,
                deleteTeamParticipant.rejected,
                createProjectTeam.fulfilled,
                createProjectTeam.rejected,
                updateProjectTeam.fulfilled,
                updateProjectTeam.rejected,
                deleteProjectTeam.fulfilled,
                deleteProjectTeam.rejected,
                copyProjectTeamToCompany.fulfilled,
                copyProjectTeamToCompany.rejected,
                addProjectTeamParticipant.fulfilled,
                addProjectTeamParticipant.rejected,
                updateProjectTeamParticipant.fulfilled,
                updateProjectTeamParticipant.rejected,
                deleteProjectTeamParticipant.fulfilled,
                deleteProjectTeamParticipant.rejected,
            ),
            (state: AdminTeamState): void => {
                state.list.uploading = false;
            },
        );
    },
});

const selectState = (state: AppState): AdminTeamState => state.admin.teams;

const emptyReportsBucket: IBaseState<Report[]> = {
    data: [],
    loading: false,
};

const AdminTeamSlice = {
    reducer: slice.reducer,
    actions: {
        getTeams,
        getTeam,
        createTeam,
        updateTeam,
        deleteTeam,
        copyTeamToProject,
        addTeamParticipant,
        getTeamParticipants,
        getTeamParticipant,
        updateTeamParticipant,
        deleteTeamParticipant,
        getProjectTeams,
        getProjectTeam,
        createProjectTeam,
        updateProjectTeam,
        deleteProjectTeam,
        copyProjectTeamToCompany,
        addProjectTeamParticipant,
        getProjectTeamParticipants,
        getProjectTeamParticipant,
        updateProjectTeamParticipant,
        deleteProjectTeamParticipant,
        getTeamReports,
        getTeamReportsByType,
        getTeamReport,
        getTeamReportResult,
        getProjectTeamReports,
        getProjectTeamReport,
        getProjectTeamReportResult,
    },
    state: {
        all: selectState,
        list: createSelector(
            selectState,
            (state: AdminTeamState) => state.list,
        ),
        detail: createSelector(
            selectState,
            (state: AdminTeamState) => state.detail,
        ),
        participantDetail: createSelector(
            selectState,
            (state: AdminTeamState) => state.participantDetail,
        ),
        reports: createSelector(
            selectState,
            (state: AdminTeamState) => state.reports,
        ),
        reportsByType: (reportType: number) =>
            createSelector(selectState, (state: AdminTeamState) => {
                const bucket = state.reportsByType[reportType];
                return bucket ?? emptyReportsBucket;
            }),
        reportDetail: createSelector(
            selectState,
            (state: AdminTeamState) => state.reportDetail,
        ),
        reportResult: createSelector(
            selectState,
            (state: AdminTeamState) => state.reportResult,
        ),
    },
};

export default AdminTeamSlice;
